import { Controller } from "@hotwired/stimulus"

// Toggles the custom-figure "Build Photos" section between the existing
// grid view and the timeline view. Both views are pre-rendered server-side
// so switching is just a visibility flip; the timeline's Stimulus
// controller is already connected, so swapping in is instantaneous.
//
// The user's choice is persisted in localStorage so the same view sticks
// across page reloads / soft navigations within the app.
export default class extends Controller {
  static targets = ["grid", "timeline", "gridBtn", "timelineBtn"]
  static values = {
    // Stable key for localStorage. Single key for now -- if we ever want
    // per-figure preferences we can append the figure id from the view.
    storageKey: { type: String, default: "customFigure.buildPhotosView" }
  }

  connect() {
    // Default to the timeline view -- it's the marquee presentation for
    // build progression. Users can still flip to grid (e.g. they prefer
    // the dense overview) and that explicit choice sticks via
    // localStorage across reloads.
    const saved = this.readPreference()
    this.show(saved === "grid" ? "grid" : "timeline")
  }

  showGrid() { this.show("grid"); this.writePreference("grid") }
  showTimeline() { this.show("timeline"); this.writePreference("timeline") }

  show(view) {
    // When there are no date-bearing photos the view's timeline scaffold
    // (and its toggle buttons) aren't rendered at all, so guard each
    // target access -- accessing a missing Stimulus target throws.
    if (!this.hasTimelineTarget) return
    const isTimeline = view === "timeline"
    if (this.hasGridTarget) this.gridTarget.classList.toggle("hidden", isTimeline)
    this.timelineTarget.classList.toggle("hidden", !isTimeline)
    if (isTimeline) {
      // The timeline controller skips its center-fade pass while its
      // container is hidden (otherwise every figure ends up dimmed and
      // gets stuck that way). Now that we're revealing it, nudge it to
      // recompute on the next frame via a synthetic scroll event -- the
      // controller already listens for those.
      requestAnimationFrame(() => window.dispatchEvent(new Event("scroll")))
    }
    if (!this.hasGridBtnTarget || !this.hasTimelineBtnTarget) return
    this.setActiveButton(isTimeline ? this.timelineBtnTarget : this.gridBtnTarget,
                         isTimeline ? this.gridBtnTarget : this.timelineBtnTarget)
  }

  setActiveButton(active, inactive) {
    if (active) {
      active.classList.add("bg-yellow-400", "text-blue-900")
      active.classList.remove("text-gray-300", "hover:bg-gray-700")
      active.setAttribute("aria-pressed", "true")
    }
    if (inactive) {
      inactive.classList.remove("bg-yellow-400", "text-blue-900")
      inactive.classList.add("text-gray-300", "hover:bg-gray-700")
      inactive.setAttribute("aria-pressed", "false")
    }
  }

  readPreference() {
    try { return window.localStorage.getItem(this.storageKeyValue) } catch (_) { return null }
  }

  writePreference(value) {
    try { window.localStorage.setItem(this.storageKeyValue, value) } catch (_) {}
  }
}
